import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowUpRight } from "lucide-react";
import Navbar from "../components/site/Navbar";
import Footer from "../components/site/Footer";
import { api } from "../lib/api";
import { useI18n } from "../context/I18nContext";
import { useSeo } from "../lib/seo";

const ease = [0.16, 1, 0.3, 1];

export default function Insights() {
  const { t, lang } = useI18n();
  const [posts, setPosts] = useState([]);

  useEffect(() => {
    api.get(`/insights?lang=${lang}`).then((r) => setPosts(r.data)).catch(() => {});
  }, [lang]);

  useSeo({
    title: "Insights · OMNIA — Design Intelligence Journal",
    description:
      lang === "es"
        ? "Ensayos, metodología y visión estratégica del ecosistema OMNIA. Donde el diseño se convierte en sistema."
        : "Essays, methodology and strategic vision from the OMNIA ecosystem. Where design becomes system.",
    type: "website",
    jsonLd: {
      "@context": "https://schema.org",
      "@type": "Blog",
      name: "OMNIA Insights",
      inLanguage: lang,
      blogPost: posts.map((p) => ({
        "@type": "BlogPosting",
        headline: p.title,
        datePublished: p.date,
        articleSection: p.category,
      })),
    },
  });

  return (
    <div className="bg-cream text-navy overflow-x-hidden">
      <Navbar />
      <section className="pt-36 pb-16 px-6 lg:px-10">
        <div className="max-w-7xl mx-auto">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, ease }}>
            <div className="overline text-navy/40 mb-6">{t("insights.overline")}</div>
            <h1 className="font-editorial text-6xl md:text-7xl tracking-tight leading-[0.95] whitespace-pre-line">
              {t("insights.h")}
            </h1>
            <p className="mt-8 max-w-xl text-lg text-navy/60 leading-relaxed">{t("insights.body")}</p>
          </motion.div>
        </div>
      </section>

      <section className="pb-28 px-6 lg:px-10">
        <div className="max-w-7xl mx-auto grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {posts.map((p, i) => (
            <motion.div key={p.slug} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }} transition={{ duration: 0.7, ease, delay: (i % 3) * 0.08 }}>
              <Link to={`/insights/${p.slug}`} data-testid={`insight-card-${p.slug}`}
                className="group block h-full bg-white border border-navy/10 hover:-translate-y-1 hover:border-navy/25 transition-all duration-500 overflow-hidden">
                <div className="aspect-[16/10] overflow-hidden bg-navy">
                  <img src={p.cover} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                </div>
                <div className="p-7">
                  <div className="flex items-center justify-between overline text-navy/40">
                    <span>{p.category}</span>
                    <span>{p.read_time} {t("insights.minRead")}</span>
                  </div>
                  <h3 className="mt-4 font-editorial text-2xl leading-tight tracking-tight">{p.title}</h3>
                  <p className="mt-3 text-sm text-navy/60 leading-relaxed">{p.excerpt}</p>
                  <div className="mt-5 inline-flex items-center gap-1.5 text-sm text-violet">
                    {t("insights.readMore")}
                    <ArrowUpRight className="w-4 h-4 group-hover:translate-x-0.5 group-hover:-translate-y-0.5 transition-transform" />
                  </div>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>
      <Footer />
    </div>
  );
}
